import { removeMdnFormatting } from "@expert/common-ui";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import { Accordion, Group, Pill, Progress, Stack, Transition } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { IconChevronDown } from "@tabler/icons-react";
import { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { MarkdownViewer } from "../../formatting";
import { activateDevice, retrieveClaim, verifyCustomer } from "../api";
import { generatePasscode } from "../api/generatePasscode";
import type { AccountMdn } from "../api/lookupMdns";
import { lookupMdns } from "../api/lookupMdns";
import { verifyPasscode } from "../api/verifyPasscode";
import type { GuideActionOption, GuideDropdownInput, GuideOption, GuideTextInput } from "../logic-builder";
import { getRoomId, navigateBack, startSimplrSession, triggerNextStep } from "../logic-builder";
import { useDelayTracking } from "../logic-builder/useDelayTracking";
import { getGuideAnalyticData, useGuideStore } from "../store";
import type { CloseGuideSource, GuideStepVariant } from "../types";
import { AskAssist } from "./AskAssist";
import { GuideActionGroup } from "./GuideActionGroup";
import classes from "./GuideCard.module.css";
import { GuideCardInput } from "./GuideCardInput";
import { GuideCloseConfirmation } from "./GuideCloseConfirmation";
import { GuideHomeConfirmation } from "./GuideHomeConfirmation";
import { GuideMenu } from "./GuideMenu";
import { GuideNavigation } from "./GuideNavigation";
import { GuideTag } from "./GuideTag";
import { markdownOverrides } from "./markdownOverrides";

interface GuideCardProps {
    actions: GuideActionOption[];
    callSid?: string;
    clientChannelId?: string;
    currentStep: string;
    dropdownInput?: GuideDropdownInput;
    hasPreviousSteps: boolean;
    logger: Logger;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    partner: Partner;
    progress: number;
    prompt?: string;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    sessionId: string;
    ssoAccessToken: string;
    tag?: string;
    textInput?: GuideTextInput;
    variant: GuideStepVariant;
}

const validateInput = (val: string, validationRegExp: string) => {
    const regex = new RegExp(validationRegExp);
    const isValid = regex.test(val);
    return isValid;
};

interface CustomerState {
    phoneNumber: string;
    imei: string;
    iccid: string;
    transactionId: string;
    accountMdns: AccountMdn[];
}

export function GuideCard({
    actions,
    callSid = "",
    clientChannelId,
    currentStep,
    dropdownInput,
    hasPreviousSteps,
    logger: loggerProp,
    onGuideClose,
    opened,
    partner,
    progress,
    prompt,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
    tag,
    textInput,
    variant,
}: GuideCardProps): JSX.Element | null {
    const logger = useMemo(() => loggerProp.child({ module: "GuideCard", supportTeam: "solve" }), [loggerProp]);
    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const [collapseValue, setCollapseValue] = useState<string | null>("opened");
    const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
    const [isHomeConfirmationOpen, setIsHomeConfirmationOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<GuideOption | undefined>(undefined);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string | undefined>();
    const [isLoadingStep, setIsLoadingStep] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [customerState, setCustomerState] = useState<CustomerState>({
        phoneNumber: "",
        imei: "",
        iccid: "",
        transactionId: "",
        accountMdns: [],
    });
    const [verifyCustomerToken, setVerifyCustomerToken] = useState("");
    const [retrieveClaimToken, setRetrieveClaimToken] = useState("");
    const [lookupAccountMdnsToken, setLookupAccountMdnsToken] = useState("");
    const [verifyPasscodeToken, setVerifyPasscodeToken] = useState("");

    const {
        errorStateSessionId,
        isErrorState,
        resetGuide,
        sessionMetadata: storedSessionMetadata, // represents local session metadata
        uiMetadata: { closeConfirmation },
        setDisableAutomaticOpen,
        setErrorStateSessionId,
    } = useGuideStore();

    const extraData = getGuideAnalyticData();
    const { completedStepCount, currentStepId, guide: flowName } = extraData;

    useDelayTracking({
        currentStepId,
        logger,
        isLoading: isLoadingStep,
        dispatcher: enhancedDispatcher,
        opened,
    });

    const handleCollapseChange = (value: string | null) => {
        logger.info(`Solve Guide | Collapse value changed to: ${value ?? "closed"}`);
        setCollapseValue(value);
        void enhancedDispatcher.dispatchBusinessEvent("Guide_CollapseChange", {
            value: value ?? "closed",
        });
    };

    const onGuideCompleted = useCallback(() => {
        logger.info({ ...extraData }, `Solve Guide | Completed guide`);
        void enhancedDispatcher.dispatchBusinessEvent(`Guide_CompletedGuide`, {
            ...extraData,
        });
        trackFullStoryEvent("Guide_CompletedGuide", {
            ...extraData,
        });
    }, [extraData, enhancedDispatcher, logger]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (progress === 100) onGuideCompleted();
    }, [progress]);

    // guide was just opened/closed so toggle loading states
    // check if an existing room exists for a given session when guide opens
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (getRoomId(sessionId) && opened) return;
        setIsLoadingStep(opened);
    }, [opened]);

    // step has loaded, so toggle loading state(s) off
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        setIsLoadingStep(false);
    }, [currentStep]);

    const handleVerifyPin = async () => {
        const payload = {
            clientChannelId,
            mdn: removeMdnFormatting(customerState.phoneNumber.replace("+1", "")),
            partner,
            pin: inputValue,
            callSid,
            sessionId,
        };
        const result = await verifyCustomer(ssoAccessToken, payload, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`VerifyCustomer`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleRetrieveClaim = async () => {
        const payload = {
            partner,
            callSid,
            sessionId,
        };
        const result = await retrieveClaim(ssoAccessToken, payload, verifyCustomerToken, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`RetrieveClaim`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleLookupAccountMdns = async () => {
        const payload = {
            clientChannelId,
            partner,
            callSid,
            sessionId,
        };
        const result = await lookupMdns(ssoAccessToken, payload, verifyCustomerToken, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`LookupAccountMDNs`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleGeneratePasscode = async (selectedMdn: string) => {
        const mdn = removeMdnFormatting(selectedMdn.replace("+1", ""));
        const payload = {
            clientChannelId,
            mdn,
            partner,
            callSid,
            sessionId,
        };
        const result = await generatePasscode(ssoAccessToken, payload, lookupAccountMdnsToken, logger);

        void enhancedDispatcher
            .withMeta({ SkipOpenSearch: true /* event contains PII, skip OpenSearch */ })
            .dispatchBusinessEvent(`GeneratePasscode`, {
                wasSuccessful: result.success,
                ...extraData,
                mdn,
            });
        return result;
    };

    const handleVerifyPasscode = async (passcode: string) => {
        const payload = {
            partner,
            callSid,
            sessionId,
            transactionId: customerState.transactionId,
            passcode,
        };
        const result = await verifyPasscode(ssoAccessToken, payload, verifyCustomerToken, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`VerifyPasscode`, {
            wasSuccessful: !!result.token,
            ...extraData,
        });
        return result;
    };

    const handleActivateDevice = async () => {
        const payload = {
            partner,
            callSid,
            sessionId,
            ...(customerState.imei
                ? { manualDeviceInfo: { imei: customerState.imei, iccid: customerState.iccid } }
                : {}),
        };
        const token = retrieveClaimToken || verifyPasscodeToken;
        const { wasSuccessful } = await activateDevice(ssoAccessToken, payload, token, logger);

        void enhancedDispatcher.dispatchBusinessEvent(`ActivateDevice`, {
            wasSuccessful,
            ...extraData,
        });
        return wasSuccessful;
    };

    const validateDropdown = () => {
        const isValid = !dropdownInput || !dropdownInput.required || selectedOption;
        if (!isValid) {
            setValidationErrorMessage(dropdownInput.validationError ?? "Please select an option");
            logger.info(
                { validationError: dropdownInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with dropdown validation error",
            );
        }
        return isValid;
    };

    const validateTextInput = () => {
        const isValid = !textInput || validateInput(inputValue, textInput.validationRegExp);
        if (!isValid) {
            setValidationErrorMessage(textInput.validationError);
            logger.info(
                { validationError: textInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with input validation error",
            );
        }
        return isValid;
    };

    const onNext = async (option: GuideActionOption) => {
        if (!validateDropdown() || !validateTextInput()) return;

        setIsLoadingStep(true);
        const buttonClickAction = option.customAction;
        let nextStep: string | undefined;
        switch (buttonClickAction) {
            case "closeGuide":
                setIsLoadingStep(false);
                onClose("cta");
                return;
            case "setCustomerState": {
                setCustomerState((custState) => ({ ...custState, [option.value]: inputValue }));
                nextStep = "Continue";
                break;
            }
            case "verifyPin": {
                const { token } = await handleVerifyPin();
                setVerifyCustomerToken(`${token}`);
                nextStep = token ? "success" : "failure";
                break;
            }
            case "retrieveClaim": {
                const { token } = await handleRetrieveClaim();
                setRetrieveClaimToken(`${token}`);
                nextStep = token ? "success" : "failure";
                break;
            }
            case "lookupAccountMdns": {
                const { success, accountMdns, token } = await handleLookupAccountMdns();
                setCustomerState((custState) => ({ ...custState, accountMdns }));
                setLookupAccountMdnsToken(token);
                nextStep = success ? "success" : "failure";
                break;
            }
            case "generatePasscode": {
                if (!selectedOption?.value) return validateDropdown();
                const { success, transactionId } = await handleGeneratePasscode(selectedOption.value);
                setCustomerState((custState) => ({ ...custState, transactionId }));
                nextStep = success ? "success" : "failure";
                break;
            }
            case "verifyPasscode": {
                const { token } = await handleVerifyPasscode(inputValue);
                setVerifyPasscodeToken(`${token}`);
                nextStep = token ? "success" : "failure";
                break;
            }
            case "activateDevice": {
                const result = await handleActivateDevice();
                nextStep = result ? "success" : "failure";
                break;
            }
            default:
                break;
        }
        const nextStepOption = selectedOption ?? option;
        logger.info(
            { option: JSON.stringify(nextStepOption), ...extraData },
            "Solve Guide | Click next (navigate to next step)",
        );
        triggerNextStep(nextStep ?? nextStepOption.value, logger);
        setSelectedOption(undefined);
        setValidationErrorMessage(undefined);
        setInputValue("");

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_NextStep`, {
            button: "NextStep",
            selectedOption: nextStepOption.value,
            ...extraData,
        });
    };

    const onBack = () => {
        logger.info({ ...extraData }, "Solve Guide | Click back (navigate to previous step)");
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_PreviousStep`, {
            button: "PreviousStep",
            ...extraData,
        });
        navigateBack({ restartFlow: false });
        setIsLoadingStep(true);
    };

    const onRestart = () => {
        const eventName = isErrorState ? `RestartErrorState` : `RestartGuide`;
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_${eventName}`, {
            button: "RestartGuide",
            ...extraData,
        });

        /*
            If the guided flow encounters the global error flow, restart the
            logic builder session with a new unique session ID based on the
            workspace session, as the previous ID is linked to the broken session.
        */
        if (isErrorState) {
            if (errorStateSessionId) {
                logger.info({ ...extraData, errorStateSessionId }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({ metadata: storedSessionMetadata, sessionId: errorStateSessionId });
            } else {
                // we should never hit this block because isErrorState and
                // errorStateSessionId get set at the same time in useGuideManager
                logger.info({ ...extraData }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({ metadata: storedSessionMetadata, sessionId: `${sessionId}_RESTARTED` });
                setErrorStateSessionId(sessionId);
            }
            setIsLoadingStep(true);
            return;
        }

        logger.info({ ...extraData }, "Solve Guide | Click return to home (navigate to first step)");
        setIsHomeConfirmationOpen(true);
    };

    const onRestartConfirmed = () => {
        logger.info({ ...extraData }, "Solve Guide | Click confirm return to home");
        setIsHomeConfirmationOpen(false);
        navigateBack({ restartFlow: true });
        resetGuide();
        setDisableAutomaticOpen(false);
        setIsLoadingStep(true);
    };

    const onClose = (source: "cta" | "navigation") => {
        logger.info({ ...extraData, source }, "Solve Guide | Click close guide");

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_CloseGuide`, {
            button: "CloseGuide",
            source,
            ...extraData,
        });

        if (progress === 100 || !closeConfirmation) {
            setDisableAutomaticOpen(true);
            onGuideClose("closeButton");
        } else {
            setIsCloseConfirmationOpen(true);
        }
    };

    const onCloseConfirmed = () => {
        logger.info({ ...extraData }, `Solve Guide | Click Confirm close guide`);
        setDisableAutomaticOpen(true);
        setIsCloseConfirmationOpen(false);
        onGuideClose("closeButton");
    };

    const onDropdownClick = (option: GuideOption) => {
        logger.info({ option, ...extraData }, `Solve Guide | Selected dropdown option`);
        setValidationErrorMessage(undefined);
        setSelectedOption(option);
    };

    /** override options with  dynamic options if applicable */
    const overrideDropdownOptions = (options: GuideOption[]) => {
        if (currentStepId === "GeneratePasscode")
            return customerState.accountMdns.map((item) => ({ label: item.masked, value: item.value }));
        else return options;
    };

    const onInputChange = (val: string) => {
        setInputValue(val);
    };

    return (
        <>
            <Transition mounted={opened} transition="fade-down" duration={400} timingFunction="ease">
                {(styles) => (
                    <Accordion
                        chevron={<IconChevronDown viewBox="0 0 20 20" />}
                        chevronPosition="left"
                        className={classes.guideCard}
                        h="auto"
                        w="100%"
                        maw="50rem"
                        style={{ ...styles, top: requiresTopPadding ? "11rem" : "1rem" }}
                        variant={variant}
                        onChange={(value) => handleCollapseChange(value)}
                        value={collapseValue}
                    >
                        <Progress
                            bg="transparent"
                            color="success.4"
                            h="0.25rem"
                            mt="0rem"
                            value={hasPreviousSteps ? progress : 0}
                        />
                        <Stack gap="0" p="sm" pt="xs" pb="xs" justify="space-between" w="100%">
                            <Accordion.Item className={classes.accordionItem} value="opened">
                                <Group w="100%" justify="space-between">
                                    <Group align="center" gap="xs">
                                        <Accordion.Control>
                                            <Pill.Group>
                                                <GuideTag>
                                                    {progress === 0 || !hasPreviousSteps
                                                        ? flowName
                                                        : `${flowName} - ${progress}%`}
                                                </GuideTag>
                                                {tag && (
                                                    <GuideTag bg="primary.4" c="var(--mantine-color-white)">
                                                        {tag}
                                                    </GuideTag>
                                                )}
                                            </Pill.Group>
                                        </Accordion.Control>
                                    </Group>
                                    <GuideNavigation
                                        hasCompletedSteps={completedStepCount > 0}
                                        isErrorState={isErrorState}
                                        isLoading={isLoadingStep}
                                        onBack={onBack}
                                        onRestart={onRestart}
                                        onClose={onClose}
                                    />
                                </Group>
                                <Accordion.Panel>
                                    <Stack gap="0rem" align="flex-start">
                                        <MarkdownViewer
                                            content={currentStep}
                                            options={{ overrides: markdownOverrides }}
                                        />
                                        {dropdownInput && (
                                            <GuideMenu
                                                label={dropdownInput.label}
                                                options={overrideDropdownOptions(dropdownInput.options)}
                                                onClick={onDropdownClick}
                                                validationError={validationErrorMessage}
                                            />
                                        )}
                                        {textInput && (
                                            <GuideCardInput
                                                textInput={textInput}
                                                value={inputValue}
                                                onInput={onInputChange}
                                                validationError={validationErrorMessage}
                                            />
                                        )}
                                        {prompt && (
                                            <AskAssist
                                                sendMessage={sendMessage}
                                                prompt={prompt}
                                                extraData={extraData}
                                            />
                                        )}
                                    </Stack>
                                    <GuideActionGroup
                                        currentStep={currentStep}
                                        isLoading={isLoadingStep}
                                        options={actions}
                                        onClick={(option) => void onNext(option)}
                                    />
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Stack>
                    </Accordion>
                )}
            </Transition>
            {closeConfirmation && (
                <GuideCloseConfirmation
                    isOpen={isCloseConfirmationOpen}
                    guideId={flowName ?? "Guided flow"}
                    title={closeConfirmation.title}
                    options={closeConfirmation.options}
                    onCancel={() => {
                        logger.info({ ...extraData }, `Solve Guide | Cancelled close guide`);
                        setIsCloseConfirmationOpen(false);
                    }}
                    onClose={onCloseConfirmed}
                    extraData={extraData}
                />
            )}
            <GuideHomeConfirmation
                isOpen={isHomeConfirmationOpen}
                onClose={() => {
                    logger.info({ ...extraData }, `Solve Guide | Cancelled return to home`);
                    setIsHomeConfirmationOpen(false);
                }}
                onSubmit={onRestartConfirmed}
                extraData={extraData}
            />
        </>
    );
}
